/**
 * Drag behaviour for the RCC Assistant floating action button.
 *
 * The FAB used to be pinned to the bottom-right corner, where it covered the
 * Footer actions (Next / Publish) on the taller workflow pages. This hook lets
 * the operator drag it anywhere on screen and remembers the position across
 * sessions in localStorage.
 *
 * Until the button is dragged for the first time no inline position is applied,
 * so the CSS default (bottom-right) keeps owning the layout.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

/** localStorage key holding the last dragged position. */
export const FAB_POSITION_STORAGE_KEY = 'rcc.agentChat.fabPosition';

/** Movement (px) below which a pointer interaction counts as a click, not a drag. */
const DRAG_THRESHOLD_PX = 4;

/** Keeps the button fully visible when the viewport shrinks. */
const MARGIN_PX = 8;

export interface FabPosition {
  /** Distance from the left edge of the viewport, px. */
  left: number;
  /** Distance from the top edge of the viewport, px. */
  top: number;
}

const isFabPosition = (value: unknown): value is FabPosition =>
  !!value
  && typeof value === 'object'
  && typeof (value as FabPosition).left === 'number'
  && typeof (value as FabPosition).top === 'number'
  && Number.isFinite((value as FabPosition).left)
  && Number.isFinite((value as FabPosition).top);

/** Clamp a position so the whole button stays inside the viewport. */
export const clampPosition = (
  position: FabPosition,
  size: { width: number; height: number }
): FabPosition => {
  const maxLeft = Math.max(MARGIN_PX, window.innerWidth - size.width - MARGIN_PX);
  const maxTop = Math.max(MARGIN_PX, window.innerHeight - size.height - MARGIN_PX);
  return {
    left: Math.min(Math.max(position.left, MARGIN_PX), maxLeft),
    top: Math.min(Math.max(position.top, MARGIN_PX), maxTop),
  };
};

const readStoredPosition = (): FabPosition | null => {
  try {
    const raw = window.localStorage.getItem(FAB_POSITION_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    return isFabPosition(parsed) ? parsed : null;
  } catch {
    // Private-mode / quota / malformed JSON — fall back to the CSS default.
    return null;
  }
};

const writeStoredPosition = (position: FabPosition): void => {
  try {
    window.localStorage.setItem(FAB_POSITION_STORAGE_KEY, JSON.stringify(position));
  } catch {
    // Persisting is best-effort; the in-memory position still applies.
  }
};

export interface DraggableFab {
  /** Ref to attach to the draggable element. */
  ref: React.RefObject<HTMLButtonElement>;
  /** Inline position, or undefined while the CSS default position is in effect. */
  style?: React.CSSProperties;
  /** True while a drag is in progress (used for the grabbing cursor). */
  isDragging: boolean;
  onPointerDown: (event: React.PointerEvent<HTMLButtonElement>) => void;
  /**
   * Wraps the button's click handler so the click that ends a drag does not also
   * open the panel.
   */
  shouldSuppressClick: () => boolean;
}

export const useDraggableFab = (): DraggableFab => {
  const ref = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState<FabPosition | null>(() => readStoredPosition());
  const [isDragging, setIsDragging] = useState(false);
  // Offset of the pointer inside the button when the drag started, so the button
  // does not jump to centre itself under the cursor.
  const grabOffsetRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  // Set once the pointer travels past the threshold — consumed by the click handler.
  const didDragRef = useRef(false);
  // Removes the in-flight drag listeners. Held in a ref so unmounting mid-drag
  // (before pointerup lands) does not leak them or set state on a dead component.
  const endDragRef = useRef<(() => void) | null>(null);

  useEffect(() => () => endDragRef.current?.(), []);

  // Clamp the current position against the live viewport. Shared by the mount
  // pass and the resize listener.
  const clampToViewport = useCallback(() => {
    setPosition((current) => {
      if (!current) {
        return current;
      }
      const element = ref.current;
      const size = element
        ? { width: element.offsetWidth, height: element.offsetHeight }
        : { width: 0, height: 0 };
      const clamped = clampPosition(current, size);
      // Returning the same object keeps React from re-rendering when nothing moved.
      return clamped.left === current.left && clamped.top === current.top ? current : clamped;
    });
  }, []);

  // Clamp once on mount: the stored position was saved against whatever viewport
  // the operator last dragged in, so a smaller window on this visit would
  // otherwise render the button off screen until the first resize event — which
  // may never come.
  useEffect(() => {
    clampToViewport();
  }, [clampToViewport]);

  // Re-clamp on resize so a position saved on a larger window can't park the
  // button off screen.
  useEffect(() => {
    window.addEventListener('resize', clampToViewport);
    return () => window.removeEventListener('resize', clampToViewport);
  }, [clampToViewport]);

  const onPointerDown = useCallback((event: React.PointerEvent<HTMLButtonElement>) => {
    // Primary button / single touch only.
    if (event.button !== 0) {
      return;
    }
    const element = ref.current;
    if (!element) {
      return;
    }
    const rect = element.getBoundingClientRect();
    grabOffsetRef.current = { x: event.clientX - rect.left, y: event.clientY - rect.top };
    didDragRef.current = false;

    const startX = event.clientX;
    const startY = event.clientY;
    const size = { width: rect.width, height: rect.height };

    const handleMove = (moveEvent: PointerEvent) => {
      const travelled = Math.hypot(moveEvent.clientX - startX, moveEvent.clientY - startY);
      if (!didDragRef.current && travelled < DRAG_THRESHOLD_PX) {
        return;
      }
      didDragRef.current = true;
      setIsDragging(true);
      setPosition(clampPosition(
        {
          left: moveEvent.clientX - grabOffsetRef.current.x,
          top: moveEvent.clientY - grabOffsetRef.current.y,
        },
        size
      ));
    };

    const removeDragListeners = () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('pointercancel', handleUp);
      endDragRef.current = null;
    };

    const handleUp = () => {
      removeDragListeners();
      setIsDragging(false);
      if (didDragRef.current) {
        setPosition((current) => {
          if (current) {
            writeStoredPosition(current);
          }
          return current;
        });
      }
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    window.addEventListener('pointercancel', handleUp);
    endDragRef.current = removeDragListeners;
  }, []);

  const shouldSuppressClick = useCallback(() => {
    if (didDragRef.current) {
      didDragRef.current = false;
      return true;
    }
    return false;
  }, []);

  return {
    ref,
    // `bottom`/`right` are reset so the inline top/left win over the SCSS default.
    style: position
      ? { left: position.left, top: position.top, bottom: 'auto', right: 'auto' }
      : undefined,
    isDragging,
    onPointerDown,
    shouldSuppressClick,
  };
};
